import { IMenuLabel } from 'src/api/getMenuLabels';

/**
 * Picker values are menu label ids, or a new label's name behind this prefix. New labels are only created
 * when the menu or menu set is saved.
 */
const NEW_MENU_LABEL_PREFIX = 'new:';

export const MENU_LABEL_NAME_MAX_LENGTH = 50;

export const toNewMenuLabelValue = (name: string): string => `${NEW_MENU_LABEL_PREFIX}${name}`;

/** The new label's name, or null when the value is an existing label's id. */
export const getNewMenuLabelName = (value: string): string | null =>
  value.startsWith(NEW_MENU_LABEL_PREFIX) ? value.slice(NEW_MENU_LABEL_PREFIX.length) : null;

export const getMenuLabelIds = (menuLabels: IMenuLabel[]): string[] =>
  menuLabels.map(({ menu_label_id }) => menu_label_id);

/** The `menu_labels` form field: existing labels by id, new ones by name. */
export const toMenuLabelsPayload = (values: string[]): string =>
  JSON.stringify(
    values.map((value) => {
      const name = getNewMenuLabelName(value);
      return name === null ? { menu_label_id: value } : { name };
    }),
  );

/** Why a typed name can't become a new label, if it can't. Names are compared trimmed and ignoring case. */
export const getMenuLabelNameError = (typedName: string, existingNames: string[]): string | undefined => {
  if (typedName === '') return undefined;
  const name = typedName.trim();
  if (!name) return 'Menu label name is required';
  if (name.length > MENU_LABEL_NAME_MAX_LENGTH) {
    return `Menu label name must be ${MENU_LABEL_NAME_MAX_LENGTH} characters or fewer`;
  }
  const lowerCaseName = name.toLocaleLowerCase();
  if (existingNames.some((existing) => existing.trim().toLocaleLowerCase() === lowerCaseName)) {
    return 'A menu label with this name already exists';
  }
  return undefined;
};

/** Whether two lists hold the same values, in any order. */
export const sameMenuLabels = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value) => b.includes(value));
